'use client';

import React, { useEffect, useState } from 'react';
import { Printer, Calendar, Droplets, Clock, UserX, ShieldAlert, XCircle, Euro } from 'lucide-react';


interface KPIData {
  totalJobs: number;
  monthJobs: number;
  totalConsumption: number;
  totalTimeSec: number;
  unlinkedPatient: number;
  unvalidated: number;
  failureRate: number;
  estimatedCost: number;
  failed: number;
}

export default function DashboardKPIs() {
  const [kpis, setKpis] = useState<KPIData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/print-jobs/kpis')
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) setKpis(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const totalHours = Math.floor((kpis?.totalTimeSec ?? 0) / 3600);
  const totalMins = Math.floor(((kpis?.totalTimeSec ?? 0) % 3600) / 60);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={`kpi-skel-${i}`} className={`rounded-xl p-5 border animate-pulse ${i === 0 ? 'col-span-2' : ''}`} style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', height: '110px' }} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Hero — Total impressions */}
      <div className="col-span-2 md:col-span-2 rounded-xl p-5 gradient-primary-glow border" style={{ backgroundColor: 'var(--card)', borderColor: 'rgba(14, 165, 233, 0.25)' }}>
        <div className="flex items-start justify-between">
          <div>
            <p className="label-field text-xs uppercase tracking-widest mb-2">Total Impressions</p>
            <p className="text-hero-metric font-tabular" style={{ color: 'var(--foreground)' }}>{kpis?.totalJobs ?? 0}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Depuis le démarrage de PrintTrace</p>
          </div>
          <div className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(14, 165, 233, 0.15)' }}>
            <Printer size={22} style={{ color: 'var(--primary)' }} />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: 'var(--success)' }}>
            +{kpis?.monthJobs ?? 0} ce mois
          </span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>AccuFab F1</span>
        </div>
      </div>
      {/* Impressions du mois */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest">Ce mois</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
            <Calendar size={16} style={{ color: 'var(--info)' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: 'var(--foreground)' }}>{kpis?.monthJobs ?? 0}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p>
      </div>
      {/* Consommation résine */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest">Résine totale</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(6, 182, 212, 0.1)' }}>
            <Droplets size={16} style={{ color: 'var(--accent)' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: 'var(--foreground)' }}>
          {(kpis?.totalConsumption ?? 0).toFixed(1)}
          <span className="text-sm font-normal ml-1" style={{ color: 'var(--muted-foreground)' }}>mL</span>
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Toutes résines confondues</p>
      </div>
      {/* Temps total */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest">Temps impression</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
            <Clock size={16} style={{ color: '#a78bfa' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: 'var(--foreground)' }}>
          {totalHours}h<span className="text-lg ml-0.5">{totalMins}m</span>
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Durée machine cumulée</p>
      </div>
      {/* Sans patient — ALERT */}
      <div className="rounded-xl p-5 border gradient-warning-glow" style={{ backgroundColor: 'var(--card)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest" style={{ color: 'rgba(245, 158, 11, 0.8)' }}>Sans patient</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)' }}>
            <UserX size={16} style={{ color: 'var(--warning)' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: 'var(--warning)' }}>{kpis?.unlinkedPatient ?? 0}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>À associer à un dossier</p>
      </div>
      {/* Non validées — ALERT */}
      <div className="rounded-xl p-5 border gradient-danger-glow" style={{ backgroundColor: 'var(--card)', borderColor: 'rgba(239, 68, 68, 0.3)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest" style={{ color: 'rgba(239, 68, 68, 0.8)' }}>Non validées</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}>
            <ShieldAlert size={16} style={{ color: 'var(--danger)' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: 'var(--danger)' }}>{kpis?.unvalidated ?? 0}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Validation requise</p>
      </div>
      {/* Taux d'échec */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest">Taux d'échec</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            <XCircle size={16} style={{ color: 'var(--danger)' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: (kpis?.failureRate ?? 0) > 10 ? 'var(--danger)' : 'var(--foreground)' }}>
          {(kpis?.failureRate ?? 0).toFixed(1)}<span className="text-lg font-normal">%</span>
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{kpis?.failed ?? 0} échec{(kpis?.failed ?? 0) > 1 ? 's' : ''} sur {kpis?.totalJobs ?? 0} impressions</p>
      </div>
      {/* Coût matière */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between mb-3">
          <p className="label-field text-xs uppercase tracking-widest">Coût matière</p>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
            <Euro size={16} style={{ color: 'var(--success)' }} />
          </div>
        </div>
        <p className="text-3xl font-bold font-tabular" style={{ color: 'var(--foreground)' }}>
          {(kpis?.estimatedCost ?? 0).toFixed(2)}<span className="text-sm font-normal ml-1" style={{ color: 'var(--muted-foreground)' }}>€</span>
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Estimation à 0,08€/mL</p>
      </div>
    </div>
  );
}